import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useSettings } from '../contexts/SettingsContext';
import { trackWhatsAppClick } from '../utils/tracking';
import { toWhatsAppPhone } from '../utils/whatsappPhone';
import { withOriginCode } from '../utils/siteEvents';

const pulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.7);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(37, 211, 102, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(37, 211, 102, 0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.8);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const FloatingButton = styled.a<{ $visible: boolean }>`
  position: fixed;
  bottom: calc(20px + var(--cookie-banner-offset, 0px));
  right: 20px;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #25d366, #128c7e);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-decoration: none;
  box-shadow: 0 4px 20px rgba(37, 211, 102, 0.4);
  z-index: 999;
  transition: all 0.3s ease;
  animation: ${pulse} 2s infinite, ${fadeIn} 0.8s ease-out;
  opacity: ${props => props.$visible ? 1 : 0};
  pointer-events: ${props => props.$visible ? 'all' : 'none'};

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 25px rgba(37, 211, 102, 0.6);
    animation: none;
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    width: 55px;
    height: 55px;
    bottom: calc(15px + var(--cookie-banner-offset, 0px));
    right: 15px;
  }
`;

const WhatsAppIcon = styled.svg`
  width: 32px;
  height: 32px;
  fill: currentColor;
  
  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
  }
`;

const Tooltip = styled.div<{ $visible: boolean }>`
  position: absolute;
  right: 70px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  white-space: nowrap;
  opacity: ${props => props.$visible ? 1 : 0};
  visibility: ${props => props.$visible ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  pointer-events: none;
  font-weight: 500;

  &::after {
    content: '';
    position: absolute;
    right: -5px;
    top: 50%;
    transform: translateY(-50%);
    border: 5px solid transparent;
    border-left-color: rgba(0, 0, 0, 0.8);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

interface WhatsAppSettings {
  whatsapp_number: string;
  whatsapp_message: string;
  whatsapp_enabled: boolean;
  company_name: string;
}

interface WhatsAppButtonProps {
  propertyId?: string;
  propertyTitle?: string;
  customMessage?: string;
  showImmediately?: boolean;
}

const WhatsAppButton: React.FC<WhatsAppButtonProps> = ({ 
  propertyId, 
  propertyTitle, 
  customMessage,
  showImmediately = true
}) => {
  const { siteSettings } = useSettings();
  const [showTooltip, setShowTooltip] = useState(false);
  const [isVisible, setIsVisible] = useState(showImmediately);

  // Mapear settings do contexto
  const settings: WhatsAppSettings | null = siteSettings ? {
    whatsapp_number: siteSettings.company_info?.whatsapp || '',
    whatsapp_message: siteSettings.whatsapp_message || 'Olá! Vim através do site e gostaria de mais informações.',
    whatsapp_enabled: siteSettings.features?.show_whatsapp_button !== false,
    company_name: siteSettings.company_info?.name || siteSettings.site_name || 'Imobiliária'
  } : null;

  useEffect(() => {
    if (showImmediately) {
      // Mostrar imediatamente se configurado
      setIsVisible(true);
      return;
    }

    // Comportamento original de scroll se não for para mostrar imediatamente
    const handleScroll = () => {
      setIsVisible(window.scrollY > 200);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [showImmediately]);

  const buildWhatsAppUrl = () => {
    if (!settings?.whatsapp_number) return '#';

    const baseUrl = window.location.origin;
    let message = customMessage || settings.whatsapp_message;

    // Substituir variáveis na mensagem
    message = message.replace('{site_url}', baseUrl);
    
    if (propertyId && propertyTitle) {
      const propertyUrl = `${baseUrl}/imovel/${propertyId}`;
      message = message.replace('{property_title}', propertyTitle);
      message = message.replace('{property_url}', propertyUrl);
    }

    const encodedMessage = encodeURIComponent(withOriginCode(message));
    const phone = toWhatsAppPhone(settings.whatsapp_number) || settings.whatsapp_number.replace(/\D/g, '');
    return `https://api.whatsapp.com/send?phone=${phone}&text=${encodedMessage}`;
  };

  if (!settings?.whatsapp_enabled || !settings?.whatsapp_number) {
    return null;
  }

  return (
    <FloatingButton
      href={buildWhatsAppUrl()}
      target="_blank"
      rel="noopener noreferrer"
      $visible={isVisible}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onClick={() => trackWhatsAppClick(propertyId ? 'property_detail' : 'floating_button')}
      title={`Falar com ${settings.company_name} no WhatsApp`}
    >
      <Tooltip $visible={showTooltip}>
        Falar com {settings.company_name}
      </Tooltip>
      
      <WhatsAppIcon viewBox="0 0 24 24">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.893 3.488"/>
      </WhatsAppIcon>
    </FloatingButton>
  );
};

export default WhatsAppButton; 
